import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import ApiAuth from "../../api/auth";
import { endsSession } from "../../api/session-failure";
import {
  SESSION_UNKNOWN,
  clearUser,
  selectSessionStatus,
  setUser,
} from "../../store/slices/user";

/**
 * LF360-1970 — ask the server who this browser is, once, at start-up.
 *
 * The session is an HttpOnly cookie, so the page cannot look at it; the only
 * way to know whether there is one is to make a request and see. That is why
 * this exists at all — it replaces reading `localStorage.user_token`, which was
 * a question the page could answer by itself right up until the credential
 * stopped being something it could hold.
 *
 * A 401 is an answer, not a failure: it is what a visitor who is not signed in
 * looks like, and the next move is the login screen. Anything else is treated
 * the same way here, because there is nothing else useful to do with a broken
 * start-up, and the request that follows will surface the real error.
 *
 * Runs once for the application rather than per route. Mounted in `App`, above
 * the router, so that every route below it — private or not — is deciding from
 * an answer instead of racing one.
 */
export const useSession = () => {
  const dispatch = useDispatch();
  const status = useSelector(selectSessionStatus);
  // The status in the store cannot stop a second request on its own: in
  // development React mounts effects twice, and both run before the first
  // answer has changed anything.
  const asked = useRef(false);

  useEffect(() => {
    if (status !== SESSION_UNKNOWN || asked.current) {
      return;
    }

    asked.current = true;

    /**
     * The answer is applied whatever happened to the component that asked for
     * it, and that is the point rather than an oversight.
     *
     * This used to carry a `cancelled` flag set from the effect's cleanup, and
     * together with the ref above it silently threw the session away. React
     * mounts, unmounts and remounts under `StrictMode`: the first run fires the
     * request and the cleanup marks it cancelled, the second run sees the ref
     * and asks nothing, and the one answer in flight lands on a closure that
     * has been told to ignore it. The session was never applied, so signing in
     * through Google — the one path with nothing but this probe to establish a
     * session — came back to the login form with a perfectly good cookie in the
     * jar.
     *
     * Nothing here touches component state; it dispatches into the store, which
     * outlives any mount. There is nothing to cancel.
     */
    const land = (res) => {
      const user = res?.data?.user;

      if (user) {
        dispatch(setUser(user));
      } else {
        dispatch(clearUser());
      }
    };

    const giveUp = () => {
      dispatch(clearUser());
    };

    ApiAuth.session()
      .then(land)
      .catch((error) => {
        /**
         * A token from before the cookie release can hide a session that is
         * perfectly good.
         *
         * `setupAxios` sends it as an `Authorization` header while it is in
         * `localStorage`, and the backend prefers a header over a cookie — a
         * credential a client attached on purpose outranks one the browser
         * attached by itself. So a browser that still holds a dead token asks
         * with the dead token, is refused, and never gets as far as the cookie
         * it just received. Signing in appears to do nothing: the callback
         * redirects, the session exists, and the login form comes back.
         *
         * Only for a refusal that names the credential — `endsSession` — and
         * only once. A 500 or a dropped connection says nothing about the
         * token, and throwing it away over a bad minute would sign out
         * everybody who has not migrated yet.
         */
        const legacyToken = localStorage.getItem("user_token");

        if (!legacyToken || !endsSession(error)) {
          return giveUp();
        }

        localStorage.removeItem("user_token");

        return ApiAuth.session().then(land).catch(giveUp);
      });
  }, [dispatch, status]);

  return status;
};

export default useSession;
